import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../i18n';

export function RafflePricesBlock() {
  const { user } = useAuth();
  const { t } = useI18n();
  const token = user?.api_token ? `Bearer ${user.api_token}` : '';
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [form, setForm] = useState({ currency: '', tickets_count: '', price: '' });

  const loadPrices = useCallback(async () => {
    if (!token) {
      setPrices([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/v1/raffles/prices', {
        method: 'GET',
        headers: { Authorization: token },
      });
      if (!res.ok) {
        throw new Error(t('Ошибка загрузки'));
      }
      const data = await res.json();
      if (data.status !== 'success') {
        throw new Error(data.error || t('Ошибка загрузки'));
      }
      setPrices(Array.isArray(data.data) ? data.data : []);
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('Ошибка загрузки'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  const handleFormChange = e => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'currency') {
      nextValue = value.toUpperCase();
    }
    setForm(f => ({ ...f, [name]: nextValue }));
  };

  const handleSave = async () => {
    const currency = form.currency.trim().toUpperCase();
    const ticketsCount = Number(form.tickets_count);
    const priceValue = Number(form.price);

    if (!currency || !Number.isInteger(ticketsCount) || ticketsCount <= 0 || !Number.isFinite(priceValue) || priceValue <= 0) {
      setSnackbar({ open: true, message: t('Заполните корректно все обязательные поля'), severity: 'error' });
      return;
    }

    const payload = { currency, tickets_count: ticketsCount, price: priceValue };

    setSaving(true);
    try {
      const res = await fetch('/api/v1/raffles/prices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status !== 'success') {
        throw new Error(data.error || t('Ошибка сохранения'));
      }
      setSnackbar({ open: true, message: t('Цена сохранена'), severity: 'success' });
      setForm(f => ({ ...f, tickets_count: '', price: '' }));
      await loadPrices();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('Ошибка сохранения'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async item => {
    if (!window.confirm(`${t('Удалить цену')} ${item.currency} ${t('на')} ${item.tickets_count} ${t('билетов')}?`)) {
      return;
    }

    setDeletingId(item.id || `${item.currency}-${item.tickets_count}-${item.series_id ?? 'default'}-${item.price}`);
    try {
      const payload = {
        currency: item.currency,
        tickets_count: item.tickets_count,
        price: Number(item.price),
      };
      if (item.series_id) {
        payload.series_id = item.series_id;
      }

      const res = await fetch('/api/v1/raffles/prices', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status !== 'success') {
        throw new Error(data.error || t('Ошибка удаления'));
      }
      setSnackbar({ open: true, message: t('Цена удалена'), severity: 'success' });
      await loadPrices();
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('Ошибка удаления'), severity: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const isSubmitDisabled =
    saving || !form.currency.trim() || !form.tickets_count.trim() || !form.price.trim();

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{t('Цены на билеты')}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 2, minWidth: 300, flex: '0 0 320px' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>{t('Добавить / обновить цену')}</Typography>
          <TextField
            label={t('Валюта')}
            name="currency"
            value={form.currency}
            onChange={handleFormChange}
            inputProps={{ maxLength: 3, style: { textTransform: 'uppercase' } }}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('Количество билетов')}
            name="tickets_count"
            value={form.tickets_count}
            onChange={handleFormChange}
            type="number"
            inputProps={{ min: 1 }}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('Цена')}
            name="price"
            value={form.price}
            onChange={handleFormChange}
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            fullWidth
            sx={{ mb: 3 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={isSubmitDisabled}
            sx={{ fontWeight: 600, minWidth: 180 }}
          >
            {saving ? t('Сохранение...') : t('Сохранить')}
          </Button>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 2, flex: '1 1 480px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('Доступные цены')}</Typography>
            <Button onClick={loadPrices} disabled={loading} size="small" variant="outlined">
              {t('Обновить')}
            </Button>
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : prices.length === 0 ? (
            <Typography sx={{ color: '#666' }}>{t('Пока нет данных.')}</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t('Валюта')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('Количество билетов')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('Цена')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Действия')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {prices.map(item => {
                    const rowId = item.id || `${item.currency}-${item.tickets_count}-${item.series_id ?? 'default'}`;
                    return (
                      <TableRow key={rowId} hover>
                        <TableCell>{item.currency}</TableCell>
                        <TableCell>{item.tickets_count}</TableCell>
                        <TableCell>{Number(item.price).toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('Удалить')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item)}
                                disabled={deletingId === rowId}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
